/**
 * Google Ads OAuth integration service (P0.3 / Path B).
 *
 * Backend routes wired in commit 7fd88c71:
 *   GET  /integrations/google-ads/link
 *   GET  /integrations/google-ads/callback   (Google calls this — not from JS)
 *   GET  /integrations/google-ads/status
 *   POST /integrations/google-ads/unlink
 *
 * Sentinel '__agency__' = MCC-level OAuth (covers all clients under the
 * agency MCC, per architecture-decisions.md#A10). Per-tenant uses real
 * tenant domain.
 */
import { apiClient } from './apiClient';
import { supabase } from './supabaseClient';

export const AGENCY_TENANT_SENTINEL = '__agency__';
export type IntegrationLevel = 'agency' | 'tenant' | null;

export interface GoogleAdsConnectionStatus {
  connected: boolean;
  level: IntegrationLevel;
  status: 'pending' | 'connected' | 'revoked' | 'error' | null;
  external_account_id: string | null;
  login_customer_id?: string | null;
  last_health_check_at: string | null;
  last_error: string | null;
  created_at?: string | null;
}

export interface UnlinkResult {
  unlinked: boolean;
  id?: string;
  reason?: string;
}

export interface GoogleAdsAccessibleCustomer {
  customer_id: string;
  formatted_customer_id: string;
  resource_name: string;
  /**
   * Account name as Google Ads reports it. The backend has always sent this
   * (accessible_customers_handler queries customer.descriptive_name); this
   * type simply dropped it, so the picker could only ever show digits.
   * Nullable — a customer can genuinely have no descriptive name set.
   */
  descriptive_name?: string | null;
  /**
   * True for manager (MCC) accounts. The backend excludes managers from
   * `recommended_customer_id` but still returns them in `customers`, so a
   * picker has to filter them out itself. Mapping a tenant to the MCC would
   * point every client-level query at the wrong account.
   */
  is_manager?: boolean;
}

/**
 * Label an account the way an operator recognises it: name first, id second.
 * Falls back to the id alone when Google has no name for the account.
 */
export function describeAccessibleCustomer(
  candidate: GoogleAdsAccessibleCustomer,
): string {
  // `?? ''` only falls through on null/undefined; a shell account whose
  // descriptive_name arrives as a number or object crashed the picker.
  const name = String(candidate.descriptive_name ?? '').trim();
  return name
    ? `${name} — ${candidate.formatted_customer_id}`
    : candidate.formatted_customer_id;
}

/** Accounts a tenant can actually be mapped to: clients, never the MCC. */
export function selectableCustomers(
  candidates: GoogleAdsAccessibleCustomer[],
): GoogleAdsAccessibleCustomer[] {
  return candidates.filter((candidate) => candidate.is_manager !== true);
}

export interface GoogleAdsAccessibleCustomersResult {
  tenant_id: string;
  level: IntegrationLevel;
  customers: GoogleAdsAccessibleCustomer[];
  recommended_customer_id: string | null;
  reason:
    | 'integration_external_account_id'
    | 'single_accessible_customer'
    | 'multiple_accessible_customers'
    | 'no_accessible_customers';
}

/**
 * Build the URL the user should be navigated to. The browser must hard-nav
 * to this URL — it returns a 302 to Google's consent screen, which a fetch
 * call cannot follow correctly.
 */
export function buildLinkUrl(params: {
  tenantId?: string; // omit or pass AGENCY_TENANT_SENTINEL for MCC-level
  workspaceId: string;
  userRole?: string;
}): string {
  const search = new URLSearchParams({
    tenant_id: params.tenantId ?? AGENCY_TENANT_SENTINEL,
    workspace_id: params.workspaceId,
    user_role: params.userRole ?? 'admin',
  });
  // apiClient.defaults.baseURL is the backend base; reuse so we hit the
  // right host (works through any rewrite/proxy the dev server has).
  const base = apiClient.defaults.baseURL?.replace(/\/$/, '') ?? '';
  return `${base}/integrations/google-ads/link?${search.toString()}`;
}

export async function getLinkUrl(params: {
  tenantId?: string;
  workspaceId: string;
  userRole?: string;
}): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error(
      'Your login session is missing. Sign out, sign in again, then reconnect Google Ads.',
    );
  }

  const { data } = await apiClient.get<{ authorization_url: string }>(
    '/integrations/google-ads/link',
    {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      params: {
        tenant_id: params.tenantId ?? AGENCY_TENANT_SENTINEL,
        workspace_id: params.workspaceId,
        user_role: params.userRole ?? 'admin',
        response: 'json',
      },
    },
  );
  return data.authorization_url;
}

export async function getConnectionStatus(
  tenantId: string,
  provider: string = 'google_ads'
): Promise<GoogleAdsConnectionStatus> {
  const { data } = await apiClient.get<GoogleAdsConnectionStatus>(
    '/integrations/google-ads/status',
    { params: { tenant_id: tenantId, provider } }
  );
  return data;
}

export async function detectAccessibleGoogleAdsCustomers(
  tenantId: string,
  provider: string = 'google_ads',
): Promise<GoogleAdsAccessibleCustomersResult> {
  const { data } = await apiClient.get<GoogleAdsAccessibleCustomersResult>(
    '/integrations/google-ads/accessible-customers',
    { params: { tenant_id: tenantId, provider } },
  );
  return data;
}

export async function unlink(params: {
  tenantId: string; // pass AGENCY_TENANT_SENTINEL for MCC-level
  provider?: string;
  userRole?: string;
}): Promise<UnlinkResult> {
  const { data } = await apiClient.post<UnlinkResult>(
    '/integrations/google-ads/unlink',
    null,
    {
      params: {
        tenant_id: params.tenantId,
        provider: params.provider ?? 'google_ads',
        user_role: params.userRole ?? 'admin',
      },
    }
  );
  return data;
}
